import { barColors, barColorsForLastTerms } from '../../constants'
import { getReportTitleDisplayItems } from '../../reportTitles'

export interface OptionType {
  name: string
  counts?: number[]
  percents?: number[]
}

export const formatSeriesDisplayName = (seriesName: string, titles: string[]): string => {
  const titleDisplayItems = getReportTitleDisplayItems(titles)
  const displayItem = titleDisplayItems.find(({ fullTitle }) => fullTitle === seriesName)
  return displayItem?.displayTitle ?? seriesName
}

export const formatTooltip = ({ params, titles, keys, options, showPercents }: {
  params: any
  titles: string[]
  keys: string[]
  options: OptionType[]
  showPercents: boolean
}): string => {
  const paramArr = Array.isArray(params) ? params : [params]
  if (!paramArr.length) return ''
  const xAxisLabel = paramArr[0].axisValue || paramArr[0].name
  const lines = paramArr.map((p: any) => {
    const groupIdx = titles.indexOf(p.seriesName)
    const optionIdx = keys.indexOf(p.axisValue || p.name)
    const actualValue = options[optionIdx]?.counts?.[groupIdx] ?? 0
    const percentValue = options[optionIdx]?.percents?.[groupIdx] ?? 0
    const displayName = formatSeriesDisplayName(p.seriesName, titles)
    let percentDisplay = ''
    if (showPercents) {
      percentDisplay = String(percentValue)
      return actualValue !== 0
        ? `${p.marker}${displayName}: <b>${actualValue}</b> (${percentDisplay}%)`
        : `${p.marker}${displayName}: <b>${actualValue}</b>`
    }
    return `${p.marker}${displayName}: <b>${actualValue}</b>`
  })
  return `
    <div style="padding:2px 6px 4px">
      <b><center>${xAxisLabel}</center></b>${lines.join('<br/>')}
    </div>
  `
}

export const shouldRenderSliceBaselineReferenceLine = (
  showSlice: boolean,
  isHorizontal: boolean
): boolean => showSlice && !isHorizontal

export const getFilteredOptions = (
  options: OptionType[],
  omitBaselineFromBars: boolean
): OptionType[] =>
  omitBaselineFromBars
    ? options.map(option => ({
      ...option,
      counts: option.counts?.slice(1),
      percents: option.percents?.slice(1)
    }))
    : options

export const getBarChartColors = (selectedTrend: number): string[] =>
  selectedTrend === 2 ? barColorsForLastTerms : barColors

export const buildBarSeries = ({
  groupCount,
  titles,
  filteredOptions,
  showPercents,
  theme,
  omitBaselineFromBars,
  barColors,
  isHorizontal = false,
  highlightedResponseAnswers = [],
  mutedBarColor = '#CDD5DF'
}: {
  groupCount: number
  titles: string[]
  filteredOptions: OptionType[]
  showPercents: boolean
  theme: any
  omitBaselineFromBars: boolean
  barColors: string[]
  isHorizontal?: boolean
  highlightedResponseAnswers?: string[]
  mutedBarColor?: string
}): any[] =>
  Array.from({ length: groupCount }, (_, groupIdx) => {
    if (omitBaselineFromBars && groupIdx === 0) return null

    // Vertical slice-only charts replace the baseline series with a custom
    // reference line, so the remaining bar indices need to shift down by one.
    // Horizontal slice-only charts keep the baseline as a normal first bar.
    const filteredIdx = omitBaselineFromBars ? groupIdx - 1 : groupIdx
    const visibleGroupCount = omitBaselineFromBars ? groupCount - 1 : groupCount

    const selectedAnswers = new Set(highlightedResponseAnswers)
    const shouldHighlightSingleSeriesBars = groupCount === 1 && selectedAnswers.size > 0

    return {
      name: titles[groupIdx],
      type: 'bar',
      data: filteredOptions.map(option => {
        const value = showPercents
          ? (option.percents?.[filteredIdx] ?? 0)
          : option.counts?.[filteredIdx] ?? 0

        if (!shouldHighlightSingleSeriesBars) {
          return value
        }

        const isSelectedAnswer = selectedAnswers.has(option.name)

        return {
          value,
          itemStyle: {
            color: isSelectedAnswer ? barColors[groupIdx % barColors.length] : mutedBarColor,
            borderRadius: isHorizontal ? [0, 10, 10, 0] : [10, 10, 0, 0]
          }
        }
      }),
      itemStyle: {
        color: barColors[groupIdx % barColors.length],
        borderRadius: isHorizontal ? [0, 10, 10, 0] : [10, 10, 0, 0]
      },
      label: {
        show: true,
        position: isHorizontal ? 'right' : 'top',
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        formatter: (params: any) => {
          if (visibleGroupCount > 1 || !params.value) return ''
          return showPercents ? `${Number(params.value).toFixed(0)}%` : `${Number(params.value).toFixed(0)}`
        }
      },
      barMaxWidth: isHorizontal ? 45 : 80
    }
  }).filter(Boolean)

export const calculateChartHeight = (
  numberOfOptions: number,
  isHorizontalLayout: boolean,
  hasLegend: boolean
): number => {
  const legendSpace = hasLegend ? 54 : 0

  if (isHorizontalLayout) {
    // Keep horizontal charts compact while preserving enough room for category labels and grouped bars.
    return Math.max(260, numberOfOptions * 34 + 96 + legendSpace)
  }

  return 400 + legendSpace
}

export const getContrastTextColor = (bgColor: string, lightText: string, darkText: string): string => {
  // Use white text for dark backgrounds, black for light backgrounds
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b
  return luminance < 140 ? lightText : darkText
}
